import React, {
  Fragment, useState, useMemo, useEffect,
} from 'react';
import {
  Transfer, Checkbox, Tooltip, Tag,
  Button,
  Switch,
} from 'antd';
import { ArrowRightOutlined, InfoCircleOutlined, OrderedListOutlined } from '@ant-design/icons';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import H3Text from '@Uilib/h3Text';

import './style.scss';

const SortableItem = React.memo(({ id, title, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#e6f7ff' : '',
    cursor: 'grab',
    padding: '8px',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '14px',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <span>
        {index + 1}
        .
        {' '}
        {title}
      </span>
    </div>
  );
});

const CustomColumnsGlossary = ({
  glossaryData, disabled, setSelectedItems, selectedItems, enable, setIsGlossaryEnabled, isGlossaryEnabled, saveGlossaryConfiguration, setSaveGlossaryConfiguration,
}) => {
  const [isDragAndDropActive, setIsDragAndDropActive] = useState(false);
  const [isUserReady, setIsUserReady] = useState(false);

  const transferData = useMemo(() => glossaryData?.map(({
    key_id, key_name, key_info, key_tag, saved_key_seq,
  }) => ({
    key: key_id,
    title: key_name, // Simplified title for filtering/searching
    description: key_info,
    key_tag,
    saved_key_seq,
  })),
  [glossaryData, selectedItems]);

  const renderItem = (item) => ({
    label: (
      <React.Fragment>
        {item.key_tag?.[0] && (
          <React.Fragment>
            <span style={{ margin: '0px 2px 0px 2px' }}>{item?.key_tag?.[0]}</span>
            <ArrowRightOutlined />
          </React.Fragment>
        )}
        <span>{item.title}</span>
        {item.key_tag?.[1] && <Tag style={{ margin: '0px 2px 0px 2px' }}>{item.key_tag[1]}</Tag>}
      </React.Fragment>
    ),
    value: item.title, // This will be used for filtering
  });

  // Initialize preselected items
  useEffect(() => {
    if (isUserReady) return;
    setIsGlossaryEnabled(enable);
    const preselectedItems = glossaryData?.reduce((acc, item) => {
      if (item?.key_selected) {
        acc[item.key_id] = {
          ...item,
          key_seq: item?.saved_key_seq || Object?.keys(acc).length + 1,
        };
      }
      return acc;
    }, {});
    setIsUserReady(true);
    setSelectedItems((prevSelectedItems) => ({
      ...prevSelectedItems,
      ...preselectedItems,
    }));
  }, [glossaryData, setSelectedItems, enable, isUserReady]);
  // Handle adding/removing items in Transfer
  const handleChange = (newTargetKeys) => {
    let seqCounter = 1; // Counter for new items without saved_key_seq

    // Step 1: Separate items with and without saved_key_seq
    const selectedWithSeq = [];
    const selectedWithoutSeq = [];

    glossaryData?.forEach((item) => {
      if (newTargetKeys.includes(item?.key_id)) {
        if (item?.saved_key_seq) {
          selectedWithSeq.push(item); // Keep existing order
        } else {
          selectedWithoutSeq.push(item); // Place at the end
        }
      }
    });

    // Step 2: Sort items with saved_key_seq in ascending order
    selectedWithSeq?.sort((a, b) => a.saved_key_seq - b.saved_key_seq);

    // Step 3: Assign correct sequence and maintain order
    const newSelectedItems = {};

    // First, add the sorted items with saved_key_seq
    selectedWithSeq?.forEach((item) => {
      newSelectedItems[item.key_id] = { ...item };
    });

    // Then, add the items without saved_key_seq at the end
    selectedWithoutSeq?.forEach((item) => {
      newSelectedItems[item.key_id] = {
        ...item,
        saved_key_seq: selectedWithSeq.length + seqCounter, // Assign at the end
      };
      seqCounter++;
    });

    setSelectedItems(newSelectedItems);
  };

  const handleCheckboxChange = (e) => {
    setIsGlossaryEnabled(e.target.checked);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id || active.id === over.id) return;

    // Convert selectedItems object to an array & sort by saved_key_seq
    const sortedItems = Object.keys(selectedItems)
      ?.map((key) => selectedItems[key])
      ?.sort((a, b) => a.saved_key_seq - b.saved_key_seq);

    // Find indexes
    const oldIndex = sortedItems.findIndex((item) => item.key_id === active.id);
    const newIndex = sortedItems.findIndex((item) => item.key_id === over.id);

    // Reorder the array using arrayMove
    const newSortedItems = arrayMove(sortedItems, oldIndex, newIndex);

    // Rebuild selectedItems object with updated key_seq
    const reorderedItems = {};
    newSortedItems?.forEach((item, index) => {
      reorderedItems[item.key_id] = {
        ...item,
        key_seq: index + 1, // Update sequence order
        saved_key_seq: index + 1, // Update saved sequence order
      };
    });

    setSelectedItems(reorderedItems);
  };

  const filterOption = (inputValue, option) => option?.title?.toLowerCase()?.includes(inputValue?.toLowerCase());

  const renderSelectedItems = useMemo(() => {
    const sortedSelectedItems = Object.keys(selectedItems)
      ?.map((key) => selectedItems[key]) // Convert object to array
      ?.sort((a, b) => a.saved_key_seq - b.saved_key_seq); // Sort by saved_key_seq

    return (
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} className="dnd-kit">
        <SortableContext
          items={sortedSelectedItems.map((item) => item.key_id)}
          strategy={verticalListSortingStrategy}
          className="sortable-context"
        >
          {sortedSelectedItems.map((item, index) => (
            <SortableItem
              key={item.key_id}
              id={item.key_id}
              title={renderItem({
                title: item.key_name,
                key_tag: item.key_tag,
              }).label}
              index={index}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  }, [selectedItems, handleDragEnd]);

  return (
    <div className={`custom-columns-glossary ${isDragAndDropActive ? 'dnd-active' : ''}`}>
      <Fragment>
        <H3Text
          text={(
            <React.Fragment>
              {' '}
              <Checkbox
                onChange={handleCheckboxChange}
                disabled={disabled}
                style={{ marginBottom: '4px', marginRight: '4px' }}
                checked={isGlossaryEnabled}
              />
              <span>Enable custom columns</span>
            </React.Fragment>
          )}
          style={{
            fontSize: '12px',
            fontWeight: '500',
          }}
          required={false}
          infoMessage="Enable this option to customize the columns displayed in your reports according to your preferences."
        />

        {isGlossaryEnabled && (
          <div
            className="update-configuration"
          >
            <Checkbox
              checked={saveGlossaryConfiguration}
              onChange={(e) => setSaveGlossaryConfiguration(e.target.checked)}
              className="update-configuration__checkbox"
            />
            {' '}
            <span style={{ marginRight: '7px', color: 'grey' }}>Save Configuration</span>
          </div>
        )}
        {isGlossaryEnabled && (
          <div
            className="update-configuration"
          >
            <Tooltip title="Enable this option to customize the sequence of columns in your reports.">
              <span style={{ marginRight: '7px', color: 'grey' }}>Change Column Sequence</span>
              <Switch checkedChildren="Enable" unCheckedChildren="Disable" checked={isDragAndDropActive} onChange={(checked) => setIsDragAndDropActive(checked)} />
            </Tooltip>
          </div>
        )}
      </Fragment>
      {isGlossaryEnabled && (
        <Fragment>
          <Transfer
            dataSource={transferData}
            showSearch
            disabled={disabled}
            titles={['Available columns', 'Selected columns']}
            targetKeys={Object?.keys(selectedItems)
              ?.map((key) => selectedItems[key]) // Convert object to array
              ?.sort((a, b) => a.saved_key_seq - b.saved_key_seq) // Sort by saved_key_seq
              ?.map((item) => item.key_id)} // Extract key_id after sorting
            onChange={handleChange}
            filterOption={filterOption}
            render={renderItem}
            listStyle={{
              width: '300px',
              height: '300px',
              marginTop: '40px',
            }}
            className="custom-transfer"
            locale={{
              notFoundContent: 'Not Found',
              itemUnit: '', // Custom label for left side
              itemsUnit: '', // Custom label for right side
            }}
          />
        </Fragment>
      )}
      {isDragAndDropActive && isGlossaryEnabled && (
        <div className="seq__changer">
          {renderSelectedItems}
        </div>
      )}
    </div>
  );
};

export default CustomColumnsGlossary;
